import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

export interface RecordingGuardValue {
  /** True while a recording is live (paused included). */
  active: boolean;
  /**
   * Resolves true when the caller may leave the page: immediately when nothing is
   * recording, otherwise after the user chooses Leave Page (the recording is stopped and
   * the microphone released first). Resolves false when they choose Continue Recording.
   */
  confirmLeave: () => Promise<boolean>;
  /**
   * Called by the recorder to report whether it is live, with a function that stops it and
   * releases the microphone. Pass `(false, null)` when it stops or unmounts.
   */
  report: (active: boolean, release: (() => void) | null) => void;
  /**
   * Registers work that must finish before a confirmed leave resolves (the browser Back
   * trap in useRecordingGuard removes its history entry here, so a Navbar navigation never
   * races it). Pass null to clear it.
   */
  registerLeaveCleanup: (cleanup: (() => Promise<void>) | null) => void;
  /**
   * True while the dialog is already open for some caller (a Navbar link, Sign out or an
   * earlier Back). The browser Back trap uses it to avoid attaching a second continuation to
   * a dialog it did not open.
   */
  isLeavePending: () => boolean;
}

// The default is inert so pages rendered without a recorder behave as before.
const INERT: RecordingGuardValue = {
  active: false,
  confirmLeave: () => Promise.resolve(true),
  report: () => {},
  registerLeaveCleanup: () => {},
  isLeavePending: () => false,
};

const RecordingGuardContext = createContext<RecordingGuardValue>(INERT);

export const useRecordingGuardContext = (): RecordingGuardValue => useContext(RecordingGuardContext);

export const RecordingGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [active, setActive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const activeRef = useRef(false);
  const releaseRef = useRef<(() => void) | null>(null);
  const settleRef = useRef<((leave: boolean) => void) | null>(null);
  const pendingRef = useRef<Promise<boolean> | null>(null);
  const leaveCleanupRef = useRef<(() => Promise<void>) | null>(null);

  const report = useCallback((next: boolean, release: (() => void) | null) => {
    activeRef.current = next;
    releaseRef.current = next ? release : null;
    setActive(next);
  }, []);

  const registerLeaveCleanup = useCallback((cleanup: (() => Promise<void>) | null) => {
    leaveCleanupRef.current = cleanup;
  }, []);

  const isLeavePending = useCallback(() => pendingRef.current !== null, []);

  const confirmLeave = useCallback((): Promise<boolean> => {
    if (!activeRef.current) return Promise.resolve(true);
    if (pendingRef.current) return pendingRef.current;
    const pending = new Promise<boolean>((resolve) => {
      settleRef.current = resolve;
    });
    pendingRef.current = pending;
    setDialogOpen(true);
    return pending;
  }, []);

  const settle = useCallback((leave: boolean) => {
    // Taken before any state update so a re-render cannot clear it first.
    const cleanup = leave ? leaveCleanupRef.current : null;
    if (leave) {
      // Stop the recorder and release the microphone before the caller navigates.
      releaseRef.current?.();
      activeRef.current = false;
      setActive(false);
    }
    setDialogOpen(false);
    const resolve = settleRef.current;
    settleRef.current = null;
    pendingRef.current = null;
    if (!cleanup) {
      resolve?.(leave);
      return;
    }
    // The caller navigates only after the cleanup has finished (or failed).
    let waiter: Promise<void>;
    try {
      waiter = Promise.resolve(cleanup());
    } catch {
      waiter = Promise.resolve();
    }
    void waiter.catch(() => undefined).then(() => resolve?.(true));
  }, []);

  const value = useMemo(
    () => ({ active, confirmLeave, report, registerLeaveCleanup, isLeavePending }),
    [active, confirmLeave, report, registerLeaveCleanup, isLeavePending],
  );

  return (
    <RecordingGuardContext.Provider value={value}>
      {children}
      <Dialog open={dialogOpen} onClose={() => settle(false)} aria-labelledby="leave-recording-title">
        <DialogTitle id="leave-recording-title">Recording in progress</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A recording is currently in progress. If you leave this page, your recording may be lost. Do you wish
            to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => settle(false)} autoFocus>
            Continue Recording
          </Button>
          <Button onClick={() => settle(true)}>Leave Page</Button>
        </DialogActions>
      </Dialog>
    </RecordingGuardContext.Provider>
  );
};
